import {
  Button,
  Card,
  Controller,
  danger,
  ErrorAlert,
  Input,
  Select,
  success,
  SwitchInput,
  UploadImageCard,
  useCanShow,
  useCustomForm,
  useEffect,
} from "@";
import { useInfo } from "./utils/hooks";
import { saveInfo } from "./utils/api";
import { cities, infoBusinessTypes } from "./utils/options";
import GoogleMapComponent from "./components/GoogleMapComponent";
import { infoDefaultValues, infoSchema } from "./utils/validation";
import { useInfoQuery } from "./utils/queries.js";

const Info = () => {
  const { data, isError, refetch } = useInfoQuery();

  // Initialize form and fetch data hooks
  const {
    reset,
    watch,
    errors,
    control,
    setValue,
    register,
    isSubmitting,
    handleSubmit,
  } = useCustomForm(infoDefaultValues, infoSchema);
  const { fillForm } = useInfo(reset, setValue);

  // Handle form submission
  const submit = async (data) => {
    try {
      await saveInfo(data);
      await success("تم الحفظ بنجاح");
    } catch (error) {}
  };

  useEffect(() => {
    fillForm(data);
  }, [data]);

  // Show error alert if there are form errors
  useEffect(() => {
    if (Object.keys(errors).length) danger("يوجد خطأ في البيانات المدخلة");
  }, [errors]);

  return (
    <>
      {/* error alert */}
      <ErrorAlert reload={refetch} show={isError} />

      <form onSubmit={handleSubmit(submit)}>
        <div className="grid gap-5 lg:grid-cols-2">
          {/* block one */}
          <div>
            {/* info */}
            <Card title="تعريفات الجمعية" className="mb-5">
              <div className="grid gap-5 md:grid-cols-2">
                {/* name */}
                <Input star label="اسم الجمعية" input={register("name")} />

                {/*seoTitle*/}
                <Input star label="SEO Title" input={register("seoTitle")} />

                {/* ShowCharityName */}
                <div className="lg:col-span-2">
                  <SwitchInput
                    label="عرض اسم الجمعية في الموقع"
                    checked={watch("showCharityName")}
                    change={(value) => setValue("showCharityName", value)}
                  />
                </div>

                {/* licenseNumber */}
                <Input
                  star
                  label="رقم الترخيص"
                  input={register("licenseNumber")}
                />

                {/* licenseEndDate */}
                <Input
                  star
                  type="datepicker"
                  label="تاريخ انتهاء الترخيص"
                  value={watch("licenseEndDate")}
                  input={register("licenseEndDate")}
                  change={(e) => setValue("licenseEndDate", e)}
                />

                {/* businessTypes */}
                <div>
                  <label className="input-label">مجالات العمل</label>
                  <Controller
                    control={control}
                    name="businessTypes"
                    render={({ field: { onChange, value, ref } }) => (
                      <Select
                        isMulti
                        inputRef={ref}
                        placeholder=""
                        options={infoBusinessTypes}
                        onChange={(val) => onChange(val.map((c) => c?.value))}
                        value={infoBusinessTypes.filter((c) =>
                          value?.includes(c?.value),
                        )}
                      />
                    )}
                  />
                </div>

                {/* postalCode */}
                <Input
                  star
                  label="الرقم البريدي"
                  input={register("postalCode")}
                />

                {/* address */}
                <Input input={register("address")} star label="العنوان" />

                {/* phone */}
                <Input
                  star
                  dir="ltr"
                  label="الهاتف"
                  error={errors.phone}
                  styles="text-start input"
                  input={register("phone")}
                />

                {/* whatsapp */}
                <Input
                  star
                  dir="ltr"
                  label="رقم الواتساب"
                  styles="text-start input"
                  error={errors.whatsapp}
                  input={register("whatsapp")}
                />

                {/* city */}
                <Input
                  star
                  type="select"
                  value={watch("city")}
                  noEmptyOption
                  label="المدينة"
                  input={register("city")}
                >
                  {cities.map((item) => (
                    <option key={item.value} value={item.label}>
                      {item.label}
                    </option>
                  ))}
                </Input>

                {/* website */}
                <Input
                  star
                  type="text"
                  error={errors.website}
                  label="الموقع الالكتروني"
                  input={register("website")}
                />

                {/* donationWebSite */}
                <Input
                  star
                  type="text"
                  label="موقع التبرعات"
                  error={errors.donationWebSite}
                  input={register("donationWebSite")}
                />

                {/* email */}
                <Input
                  star
                  error={errors.email}
                  label="البريد الالكتروني"
                  input={register("email")}
                />

                {/* charityFooter */}
                <Input
                  star
                  label="نص تذييل الموقع"
                  input={register("charityFooter")}
                />

                {/* summary */}
                <div className="md:col-span-2">
                  <Input
                    star
                    type="textarea"
                    error={errors.summary}
                    label="نبذة عن الجمعية"
                    input={register("summary")}
                  />
                </div>
              </div>
            </Card>
          </div>

          {/* block two */}
          <div>
            {/* social links */}
            <Card
              isOpen={false}
              title="بيانات التواصل الاجتماعي"
              className="mb-5"
            >
              <div className="grid grid-cols-1 gap-5">
                {/* snapChat */}
                <Input
                  star
                  label="حساب سناب شات"
                  error={errors.snapChat}
                  input={register("snapChat")}
                />

                {/* tiktok */}
                <Input
                  star
                  label="حساب تيك توك"
                  error={errors.tiktok}
                  input={register("tiktok")}
                />

                {/* faceBook */}
                <Input
                  star
                  label="حساب الفيس بوك"
                  error={errors.faceBook}
                  input={register("faceBook")}
                />

                {/* twitter */}
                <Input
                  star
                  label="حساب تويتر"
                  error={errors.twitter}
                  input={register("twitter")}
                />

                {/* youtube */}
                <Input
                  star
                  label="حساب يوتيوب"
                  error={errors.youtube}
                  input={register("youtube")}
                />

                {/* instagram */}
                <Input
                  star
                  label="حساب الانستغرام"
                  error={errors.instagram}
                  input={register("instagram")}
                />
              </div>
            </Card>

            {/* images */}
            <Card isOpen={false} title="صور الجمعية">
              <div className="grid grid-cols-1">
                {/* logo */}
                <UploadImageCard
                  show
                  title="شعار الجمعية (يظهر في الفواتير المطبوعات)"
                  src={watch("charityLogo")}
                  error={errors.logo?.message}
                  upload={(file) => setValue("logo", file)}
                />

                {/* Fav Icon */}
                <UploadImageCard
                  show
                  title="Fav Icon"
                  src={watch("favIconLink")}
                  error={errors.favIcon?.message}
                  upload={(file) => setValue("favIcon", file)}
                />

                {/* charity seal */}
                <UploadImageCard
                  show
                  title="ختم الجمعية"
                  src={watch("charitySignature")}
                  error={errors.charitySignature?.message}
                  upload={(file) => setValue("charitySignature", file)}
                />
              </div>
            </Card>

            {/* map */}
            <Card title="العنوان في الخريطة">
              <label className="input-label mb-3">العنوان على الخريطة</label>
              <GoogleMapComponent
                initialCenter={watch("locationOnMap")}
                onLocationSelect={(location) =>
                  setValue("locationOnMap", JSON.stringify(location))
                }
              />
            </Card>
          </div>
        </div>

        {useCanShow("editAssociationData") && (
          <div className="floating-btn">
            <Button
              title="حفظ البيانات"
              type="submit"
              isLoading={isSubmitting}
            />
          </div>
        )}
      </form>
    </>
  );
};

export default Info;
